import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { ComponentPropsWithoutRef, ElementRef, forwardRef, MouseEvent, ReactNode } from 'react';
import { Button, buttonVariants } from 'src/shared/components/ui/buttons/button';
import SaveButton from 'src/shared/components/ui/buttons/save-button';
import ConditionalParent from 'src/shared/components/utils/ConditionalParent';
import { cn } from 'src/shared/lib/utils';
import { testId } from 'tests/e2e/utils/test-ids.constant';

const BaseModal = DialogPrimitive.Root;
const BaseModalTrigger = DialogPrimitive.Trigger;

const BaseModalPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => <DialogPrimitive.Portal {...props} />;
BaseModalPortal.displayName = 'BaseModalPortal';

const BaseModalOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-muted/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
BaseModalOverlay.displayName = 'BaseModalOverlay';

interface BaseModelContentProps extends Omit<ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'onSubmit'> {
  subtleTitle: ReactNode;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  autoClose?: boolean;
  actions?: ReactNode;
  submitLoading?: boolean;
  confirmDisabled?: boolean;
  hideClose?: boolean;
  hideConfirm?: boolean;
  cancelText?: string;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  onConfirm?: (e: MouseEvent) => void;
  onCancel?: (e: MouseEvent) => void;
}

function BaseModalContent({
  title,
  subtleTitle,
  description,
  cancelText,
  children,
  actions,
  className,
  autoClose = false,
  confirmText = 'Confirm',
  submitLoading = false,
  confirmDisabled = false,
  hideClose = false,
  hideConfirm = false,
  variant = 'default',
  onConfirm,
  onCancel,
  ...props
}: BaseModelContentProps) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    const target = event.target as HTMLElement;
    const isEditable =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable;

    if (isEditable) {
      return;
    }

    if (confirmDisabled || submitLoading || hideConfirm) {
      return;
    }

    event.preventDefault();
    onConfirm?.(event as unknown as MouseEvent);
  };

  return (
    <BaseModalPortal>
      <BaseModalOverlay />
      <VisuallyHidden>
        <DialogPrimitive.DialogTitle>{title}</DialogPrimitive.DialogTitle>
        <DialogPrimitive.DialogDescription>{description}</DialogPrimitive.DialogDescription>
      </VisuallyHidden>
      <DialogPrimitive.Content
        className={cn(
          `py-[14px] px-4 min-w-[600px] h-fit fixed left-[50%] top-[50%] z-50 flex flex-col translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background 
          shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
          data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
          data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm max-h-[90%] max-w-[90%]`,
          className
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <DialogPrimitive.Close className="absolute right-3 top-3 h-6 w-6 flex-center rounded-sm hover:bg-neutral-100 transition-colors text-typography-grey hover:text-typography-black focus:outline-none disabled:pointer-events-none">
          <X size={16} />
        </DialogPrimitive.Close>
        <div className="text-sm text-typography-grey">{subtleTitle}</div>
        <div className="flex items-center justify-between">
          <div className="mx-2 overflow-hidden grow">
            <p className="text-xl font-bold text-neutral-800 overflow-hidden text-ellipsis max-w-full">{title}</p>
            <p className="mt-1 text-typography-darkgrey font-light text-[15px] leading-normal max-w-[85%]">
              {description}
            </p>
          </div>
          {actions && <div className="flex items-center gap-2 mr-2">{actions}</div>}
        </div>
        <div className="mx-2 my-1 overflow-hidden max-h-full flex flex-col grow">{children}</div>
        <div className="flex w-full justify-end gap-2 p-2">
          {!hideClose && (
            <DialogPrimitive.DialogClose asChild onClick={onCancel}>
              <Button variant="ghost" data-testid={testId.modals.cancelButton}>
                {cancelText || 'Cancel'}
              </Button>
            </DialogPrimitive.DialogClose>
          )}
          <ConditionalParent
            condition={autoClose}
            parent={(children) => <DialogPrimitive.DialogClose asChild>{children}</DialogPrimitive.DialogClose>}
          >
            {!hideConfirm && (
              <SaveButton
                loading={submitLoading}
                disabled={confirmDisabled}
                variant={variant}
                onClick={onConfirm}
                data-testid={testId.modals.confirmButton}
              >
                {confirmText}
              </SaveButton>
            )}
          </ConditionalParent>
        </div>
      </DialogPrimitive.Content>
    </BaseModalPortal>
  );
}

interface RawModalProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string;
}

function RawModal({ className, children, overlayClassName, ...props }: RawModalProps) {
  return (
    <BaseModalPortal>
      <VisuallyHidden>
        <DialogPrimitive.DialogTitle />
        <DialogPrimitive.DialogDescription />
      </VisuallyHidden>
      <BaseModalOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        className={cn(
          `p-0 min-w-[600px] h-fit fixed left-[50%] top-[50%] z-50 flex flex-col translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background 
        shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
        data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
        data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm max-h-[90%] max-w-[90%]`,
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </BaseModalPortal>
  );
}

export { BaseModal, BaseModalContent, BaseModalTrigger, RawModal };
